import { useState, type ChangeEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/PageHeader';
import {
  customerCreateSchema,
  deriveSlugFromName,
  type CustomerCreateForm,
} from '@/features/admin-customers/schema';
import { useCreateCustomer } from '@/features/admin-customers/api';
import { generatePassword } from '@/features/admin-customers/password';
import { ProductScopePicker } from '@/features/admin-customers/components/ProductScopePicker';
import { ApiError } from '@/lib/api/client';

export function CustomerCreate() {
  const navigate = useNavigate();
  const createMutation = useCreateCustomer();
  const [scopes, setScopes] = useState<string[]>([]);
  // Only auto-fill the slug from the name while the admin hasn't touched it.
  // Once they edit the slug field, leave it alone.
  const [slugTouched, setSlugTouched] = useState(false);

  const form = useForm<CustomerCreateForm>({
    resolver: zodResolver(customerCreateSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      companySlug: '',
      productScopes: [],
    },
  });

  const password = form.watch('password');

  const onGeneratePassword = () => {
    const generated = generatePassword(16);
    form.setValue('password', generated, { shouldValidate: true });
  };

  const onScopesChange = (next: string[]) => {
    setScopes(next);
    form.setValue('productScopes', next);
  };

  const onSubmit = form.handleSubmit(async (values) => {
    // RHF + zodResolver returns the *input* type, so required fields type as
    // optional. Schema validation guarantees they're populated before we get here.
    const payload = {
      name: values.name as string,
      email: values.email as string,
      password: values.password as string,
      companySlug: values.companySlug as string,
      productScopes: scopes,
    };
    try {
      await createMutation.mutateAsync(payload);
      try {
        await navigator.clipboard.writeText(payload.password);
        toast.success('Customer created — password copied to clipboard');
      } catch {
        toast.success('Customer created (clipboard unavailable — copy password manually)');
      }
      navigate('/admin/customers');
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to create customer';
      toast.error(msg);
    }
  });

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/admin/customers">
            <ArrowLeft className="h-4 w-4" />
            Back to customers
          </Link>
        </Button>
      </div>
      <PageHeader title="New customer" />

      <Card>
        <CardContent className="pt-6">
          <Form {...form}>
            <form onSubmit={onSubmit} className="space-y-4" data-testid="admin-customer-new-form">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => {
                  const onNameChange = (e: ChangeEvent<HTMLInputElement>) => {
                    field.onChange(e);
                    if (!slugTouched) {
                      form.setValue('companySlug', deriveSlugFromName(e.target.value), {
                        shouldValidate: false,
                      });
                    }
                  };
                  return (
                    <FormItem>
                      <FormLabel>Name</FormLabel>
                      <FormControl>
                        <Input
                          data-testid="admin-customer-new-name"
                          {...field}
                          onChange={onNameChange}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" data-testid="admin-customer-new-email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="companySlug"
                render={({ field }) => {
                  const onSlugChange = (e: ChangeEvent<HTMLInputElement>) => {
                    setSlugTouched(true);
                    field.onChange(e);
                  };
                  return (
                    <FormItem>
                      <FormLabel>Company slug</FormLabel>
                      <FormControl>
                        <Input
                          data-testid="admin-customer-new-slug"
                          placeholder="e.g. archer"
                          {...field}
                          onChange={onSlugChange}
                        />
                      </FormControl>
                      <FormDescription>
                        Kebab-case, used for Gitea labelling — e.g. <code>archer</code>.
                        Auto-derived from the name; edit if you need a different slug.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Initial password</FormLabel>
                    <div className="flex gap-2">
                      <FormControl>
                        <Input data-testid="admin-customer-new-password" {...field} />
                      </FormControl>
                      <Button type="button" variant="outline" onClick={onGeneratePassword}>
                        <RefreshCw className="h-4 w-4" />
                        Generate
                      </Button>
                    </div>
                    {password && (
                      <FormDescription>
                        Password will be copied to your clipboard after submit.
                      </FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-1.5">
                <Label>Product scopes</Label>
                <ProductScopePicker value={scopes} onChange={onScopesChange} />
              </div>

              <div className="flex justify-end gap-2 border-t pt-4">
                <Button type="button" variant="outline" asChild>
                  <Link to="/admin/customers">Cancel</Link>
                </Button>
                <Button
                  type="submit"
                  data-testid="admin-customer-new-submit"
                  disabled={form.formState.isSubmitting}
                >
                  {form.formState.isSubmitting ? 'Creating…' : 'Create customer'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
